import React from "react";

import { createTheme, ThemeProvider } from "@mui/material";
import type {} from "@mui/x-data-grid/themeAugmentation";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    bold20: React.CSSProperties;
    bold18: React.CSSProperties;
    bold16: React.CSSProperties;
    bold15: React.CSSProperties;
    bold14: React.CSSProperties;
    bold13: React.CSSProperties;
    bold12: React.CSSProperties;
    regular20: React.CSSProperties;
    regular18: React.CSSProperties;
    regular16: React.CSSProperties;
    regular15: React.CSSProperties;
    regular14: React.CSSProperties;
    regular13: React.CSSProperties;
    regular12: React.CSSProperties;
    regular10: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bold20: React.CSSProperties;
    bold18: React.CSSProperties;
    bold16: React.CSSProperties;
    bold15: React.CSSProperties;
    bold14: React.CSSProperties;
    bold13: React.CSSProperties;
    bold12: React.CSSProperties;
    regular20: React.CSSProperties;
    regular18: React.CSSProperties;
    regular16: React.CSSProperties;
    regular15: React.CSSProperties;
    regular14: React.CSSProperties;
    regular13: React.CSSProperties;
    regular12: React.CSSProperties;
    regular10: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bold20: true;
    bold18: true;
    bold16: true;
    bold15: true;
    bold14: true;
    bold13: true;
    bold12: true;
    regular20: true;
    regular18: true;
    regular16: true;
    regular15: true;
    regular14: true;
    regular13: true;
    regular12: true;
    regular10: true;
  }
}

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2448BA",
    },
    secondary: {
      main: "#355CD7",
    },
    grey: {
      300: "#F7F8FB",
      400: "#DDDDE0",
      500: "#C5C5C5",
      600: "#6F6F70",
    },
    background: {
      default: "#FFFFFF",
      paper: "#F5F7FD",
    },
    text: {
      primary: "#000000",
    },
  },
  typography: {
    bold20: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: "30px",
    },
    bold18: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: "24px",
    },
    bold16: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: "24px",
    },
    bold15: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: "22.5px",
    },
    bold14: {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: "21px",
    },
    bold13: {
      fontSize: 13,
      fontWeight: 700,
      lineHeight: "19.5px",
    },
    bold12: {
      fontSize: 12,
      fontWeight: 700,
      lineHeight: "18px",
    },
    regular20: {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: "30px",
    },
    regular18: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: "24px",
    },
    regular16: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: "24px",
    },
    regular15: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: "22.5px",
    },
    regular14: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "21px",
    },
    regular13: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: "19.5px",
    },
    regular12: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "18px",
    },
    regular10: {
      fontSize: 10,
      fontWeight: 400,
      lineHeight: "15px",
    },
  },
  // MuiButtonBase-root-MuiCheckbox-root
  components: {
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: ({ _, theme }) => ({
          ...{
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: () => ({
          ...{
            boxShadow: "none"
          },
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "h1",
          h2: "h2",
          h3: "h3",
          bold20: "p",
          bold18: "p",
          bold16: "p",
          bold15: "p",
          bold14: "p",
          bold13: "p",
          bold12: "p",
          regular20: "p",
          regular18: "p",
          regular16: "p",
          regular15: "p",
          regular14: "p",
          regular13: "p",
          regular12: "p",
        },
      },
      styleOverrides: {
        h1: {
          fontSize: 32,
          fontWeight: 700,
          lineHeight: "150%",
        },
        h2: {
          fontSize: 28,
          fontWeight: 700,
          lineHeight: "150%",
        },
        h3: {
          fontSize: 24,
          fontWeight: 700,
          lineHeight: "150%",
        }
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ _, theme }) => ({
          ...{
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ _, theme }) => ({
          ...{
            color: theme.palette.grey[400],
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: "12px 16px",
        },
      },
    },
  },
});

type ThemeProviderWrapProps = React.PropsWithChildren<{}>;

export const ThemeProviderWrap: React.FC<ThemeProviderWrapProps> = ({
  children,
}) => <ThemeProvider theme={theme}>{children}</ThemeProvider>;
